import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jobsAPI, jobTemplatesAPI } from '../api/api';
import axiosInstance from '../api/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import VoiceDictateButton from '../components/VoiceDictateButton';
import { 
  Briefcase, 
  MapPin, 
  CurrencyDollar, 
  Clock,
  Plus,
  X,
  ArrowLeft,
  CheckCircle,
  Warning,
  SpinnerGap,
  PaperPlaneTilt,
  Wrench,
  Copy
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { BreadcrumbsInline } from '../components/Breadcrumbs';

const PostJob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPlan = location.state?.fromPlan || null;
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [skillInput, setSkillInput] = useState('');
  
  const [formData, setFormData] = useState({
    title: fromPlan?.title || '',
    description: fromPlan?.description || '',
    location: '',
    budget_min: fromPlan?.budget ? String(Math.round(fromPlan.budget * 0.8)) : '',
    budget_max: fromPlan?.budget ? String(Math.round(fromPlan.budget)) : '',
    duration: '',
    required_skills: [],
    open_to: 'workers',
    parent_job_id: '',
    featured: false
  });

  const [errors, setErrors] = useState({});
  const [parentOptions, setParentOptions] = useState([]);

  useEffect(() => {
    const loadParents = async () => {
      try {
        const r = await axiosInstance.get('/applications/my-applications');
        const accepted = (r.data.applications || []).filter((a) => a.status === 'accepted' && a.job_title);
        setParentOptions(accepted.map((a) => ({ job_id: a.job_id, job_title: a.job_title })));
      } catch {}
    };
    loadParents();
  }, []);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token) {
      navigate('/login?type=contractor');
      return;
    }
    
    // Quick check from localStorage first
    if (userType && userType !== 'contractor') {
      toast({
        title: 'Access Denied',
        description: 'Only contractors can post jobs. Please log in with a contractor account.',
        variant: 'destructive'
      });
      navigate('/');
      return;
    }
    
    // Verify user type from API for robustness
    const verifyUserType = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // API returns {user: {user_type: ...}}
          const actualUserType = data.user?.user_type || data.user_type;
          if (actualUserType !== 'contractor') {
            toast({
              title: 'Access Denied',
              description: 'Only contractors can post jobs. Please log in with a contractor account.',
              variant: 'destructive'
            });
            // Update localStorage with correct value
            localStorage.setItem('userType', actualUserType);
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error verifying user:', error);
      }
    };
    
    verifyUserType();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.required_skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        required_skills: [...prev.required_skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      required_skills: prev.required_skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Job title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.trim().length < 50) newErrors.description = 'Description must be at least 50 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.budget_min) newErrors.budget_min = 'Minimum budget is required';
    if (!formData.budget_max) newErrors.budget_max = 'Maximum budget is required';
    if (Number(formData.budget_min) > Number(formData.budget_max)) {
      newErrors.budget_max = 'Max budget must be greater than min';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const res = await jobsAPI.create({
        ...formData,
        parent_job_id: formData.parent_job_id || null,
        budget_min: Number(formData.budget_min),
        budget_max: Number(formData.budget_max)
      });
      
      if (fromPlan?.id) {
        const newJobId = res.data?.id || res.data?._id;
        if (newJobId) {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/project-plans/${fromPlan.id}/link-job`,
              { job_id: newJobId },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
          } catch { /* linking is best-effort */ }
        }
      }
      
      toast({
        title: 'Job Posted!',
        description: fromPlan ? 'Your job is live and linked to your project plan.' : 'Your job has been published successfully.',
      });
      
      navigate('/my-jobs');
    } catch (error) {
      console.error('Error posting job:', error);
      const errorDetail = error.response?.data?.detail;
      
      // Check for plan limit error
      if (error.response?.status === 403 && errorDetail?.upgrade_required) {
        toast({
          title: 'Plan Limit Reached',
          description: errorDetail.message || 'You have reached your job posting limit. Upgrade your plan to post more jobs.',
          variant: 'destructive'
        });
        // Offer to navigate to pricing
        setTimeout(() => {
          if (window.confirm('Would you like to upgrade your plan?')) {
            navigate('/#pricing');
          }
        }, 500);
      } else if (error.response?.status === 403) {
        // Permission error (not a contractor)
        toast({
          title: 'Access Denied',
          description: 'Only contractors can post jobs. Please log in with a contractor account.',
          variant: 'destructive'
        });
        localStorage.removeItem('userType');
        setTimeout(() => navigate('/login?type=contractor'), 2000);
      } else {
        toast({
          title: 'Error',
          description: typeof errorDetail === 'string' ? errorDetail : 'Failed to post job',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const suggestedSkills = [
    'Carpentry', 'Plumbing', 'Electrical', 'HVAC', 'Roofing',
    'Concrete', 'Drywall', 'Painting', 'Flooring', 'Welding'
  ];

  const handleSaveAsTemplate = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast({ title: 'Fill in title & description first', variant: 'destructive' });
      return;
    }
    setSavingTemplate(true);
    try {
      await jobTemplatesAPI.create({
        title: formData.title,
        description: formData.description,
        location: formData.location,
        budget_min: formData.budget_min ? Number(formData.budget_min) : null,
        budget_max: formData.budget_max ? Number(formData.budget_max) : null,
        duration: formData.duration,
        required_skills: formData.required_skills,
      });
      toast({ title: 'Saved as template!', description: 'Find it in your Job Templates page.' });
    } catch {
      toast({ title: 'Error saving template', variant: 'destructive' });
    } finally { setSavingTemplate(false); }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="post-job-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-[#2B4C6F]">
        <div className="max-w-3xl mx-auto px-4">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/contractor-dashboard' },
              { label: 'My Jobs', href: '/my-jobs' },
              { label: 'Post Job' }
            ]}
            className="mb-3"
          />
          <h1 className="text-3xl md:text-4xl font-black">Post a Job</h1>
          <p className="text-blue-100 text-lg mt-1">Find skilled workers for your project</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg border-2 ${
                step >= s 
                  ? 'bg-[#2B4C6F] text-white border-black' 
                  : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 border-neutral-300 dark:border-neutral-600'
              }`}>
                {step > s ? <CheckCircle size={24} weight="fill" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-20 md:w-32 h-2 mx-2 rounded ${
                  step > s ? 'bg-[#2B4C6F]' : 'bg-neutral-200 dark:bg-neutral-700'
                }`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Job Details */}
          {step === 1 && (
            <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/30 rounded-xl flex items-center justify-center border-2 border-[#2B4C6F]">
                  <Briefcase size={24} weight="bold" className="text-[#2B4C6F]" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-neutral-900 dark:text-white">Job Details</h2>
                  <p className="text-neutral-500 dark:text-neutral-400">What work do you need done?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Job Title *</Label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Kitchen Renovation, Roof Repair"
                    className={`h-14 text-lg border-2 rounded-xl dark:bg-neutral-700 dark:text-white ${errors.title ? 'border-red-500' : 'border-black dark:border-neutral-600'}`}
                    data-testid="job-title-input"
                  />
                  {errors.title && (
                    <p className="text-red-600 font-bold mt-2 flex items-center gap-2">
                      <Warning size={20} weight="fill" /> {errors.title}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Who can apply?</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="open-to-selector">
                    {[
                      { value: 'workers', label: 'Workers', desc: 'Skilled tradespeople' },
                      { value: 'subcontractors', label: 'Subcontractors', desc: 'Licensed contractors' },
                      { value: 'both', label: 'Both', desc: 'Widest reach' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, open_to: opt.value }))}
                        className={`p-3 rounded-xl border-2 text-left transition-colors ${
                          formData.open_to === opt.value
                            ? 'border-[#EA580C] bg-orange-50 dark:bg-orange-950/30'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-400'
                        }`}
                        data-testid={`open-to-${opt.value}`}
                      >
                        <p className="font-bold text-neutral-900 dark:text-white">{opt.label}</p>
                        <p className="text-xs text-neutral-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {parentOptions.length > 0 && (
                  <div data-testid="parent-job-section">
                    <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Part of a project you're subbing on? <span className="text-sm font-normal text-neutral-500">(optional)</span></Label>
                    <select
                      value={formData.parent_job_id}
                      onChange={(e) => setFormData((p) => ({ ...p, parent_job_id: e.target.value }))}
                      className="w-full h-12 px-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
                      data-testid="parent-job-select"
                    >
                      <option value="">Standalone job — not linked</option>
                      {parentOptions.map((o) => (
                        <option key={o.job_id} value={o.job_id}>{o.job_title}</option>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-500 mt-1">Links this posting to the parent project so your chain of subs stays organized.</p>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-lg font-bold block text-neutral-900 dark:text-white">Description *</Label>
                    <VoiceDictateButton
                      mode="note"
                      onResult={({ transcript }) => {
                        if (!transcript) return;
                        setFormData((prev) => ({ ...prev, description: `${prev.description}${prev.description.trim() ? '\n' : ''}${transcript}` }));
                      }}
                    />
                  </div>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe the work needed, materials, timeline expectations..."
                    rows={5}
                    className={`text-lg border-2 rounded-xl dark:bg-neutral-700 dark:text-white ${errors.description ? 'border-red-500' : 'border-black dark:border-neutral-600'}`}
                    data-testid="job-description-input"
                  />
                  <div className="flex justify-between mt-2">
                    {errors.description ? (
                      <p className="text-red-600 font-bold flex items-center gap-2">
                        <Warning size={20} weight="fill" /> {errors.description}
                      </p>
                    ) : <span />}
                    <span className={`font-bold ${formData.description.length >= 50 ? 'text-green-600' : 'text-neutral-400'}`}>
                      {formData.description.length}/50
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNext}
                className="w-full h-16 mt-6 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="next-step-btn"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Location & Budget */}
          {step === 2 && (
            <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center border-2 border-emerald-300 dark:border-emerald-600">
                  <CurrencyDollar size={24} weight="bold" className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-neutral-900 dark:text-white">Location & Budget</h2>
                  <p className="text-neutral-500 dark:text-neutral-400">Where and how much?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Location *</Label>
                  <div className="relative">
                    <MapPin size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City, State"
                      className={`h-14 text-lg border-2 rounded-xl pl-12 dark:bg-neutral-700 dark:text-white ${errors.location ? 'border-red-500' : 'border-black dark:border-neutral-600'}`}
                      data-testid="job-location-input"
                    />
                  </div>
                  {errors.location && (
                    <p className="text-red-600 font-bold mt-2 flex items-center gap-2">
                      <Warning size={20} weight="fill" /> {errors.location}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Min Budget *</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 font-bold">$</span>
                      <Input
                        name="budget_min"
                        type="number"
                        value={formData.budget_min}
                        onChange={handleChange}
                        placeholder="1,000"
                        className={`h-14 text-lg border-2 rounded-xl pl-8 dark:bg-neutral-700 dark:text-white ${errors.budget_min ? 'border-red-500' : 'border-black dark:border-neutral-600'}`}
                        data-testid="budget-min-input"
                      />
                    </div>
                    {errors.budget_min && (
                      <p className="text-red-600 font-bold mt-2 text-sm">{errors.budget_min}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Max Budget *</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 font-bold">$</span>
                      <Input
                        name="budget_max"
                        type="number"
                        value={formData.budget_max}
                        onChange={handleChange}
                        placeholder="5,000"
                        className={`h-14 text-lg border-2 rounded-xl pl-8 dark:bg-neutral-700 dark:text-white ${errors.budget_max ? 'border-red-500' : 'border-black dark:border-neutral-600'}`}
                        data-testid="budget-max-input"
                      />
                    </div>
                    {errors.budget_max && (
                      <p className="text-red-600 font-bold mt-2 text-sm">{errors.budget_max}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Duration</Label>
                  <div className="relative">
                    <Clock size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="e.g., 2-3 weeks"
                      className="h-14 text-lg border-2 border-black dark:border-neutral-600 rounded-xl pl-12 dark:bg-neutral-700 dark:text-white"
                      data-testid="job-duration-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 h-14 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold text-lg rounded-xl border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="next-step-btn"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Skills & Review */}
          {step === 3 && (
            <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center border-2 border-purple-300 dark:border-purple-600">
                  <Wrench size={24} weight="bold" className="text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-neutral-900 dark:text-white">Required Skills</h2>
                  <p className="text-neutral-500 dark:text-neutral-400">What skills do you need?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-lg font-bold mb-2 block text-neutral-900 dark:text-white">Add Skills</Label>
                  <div className="flex gap-2">
                    <Input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a skill..."
                      className="h-14 text-lg border-2 border-black dark:border-neutral-600 rounded-xl flex-1 dark:bg-neutral-700 dark:text-white"
                      data-testid="skill-input"
                    />
                    <button
                      type="button"
                      onClick={handleAddSkill}
                      className="h-14 w-14 flex items-center justify-center bg-[#2B4C6F] text-white rounded-xl border-2 border-black"
                    >
                      <Plus size={24} weight="bold" />
                    </button>
                  </div>
                </div>

                {/* Suggested Skills */}
                <div>
                  <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400 mb-2">Quick Add:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedSkills.filter(s => !formData.required_skills.includes(s)).slice(0, 6).map((skill) => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          required_skills: [...prev.required_skills, skill]
                        }))}
                        className="px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-full border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                      >
                        + {skill}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Skills */}
                {formData.required_skills.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400 mb-2">Selected Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.required_skills.map((skill) => (
                        <span
                          key={skill}
                          className="flex items-center gap-2 px-4 py-2 bg-[#2B4C6F] text-white font-bold rounded-full border-2 border-black"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(skill)}
                            className="hover:bg-white/20 rounded-full p-0.5"
                          >
                            <X size={16} weight="bold" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Job Summary */}
                <div className="bg-neutral-50 dark:bg-neutral-700 border-2 border-neutral-200 dark:border-neutral-600 rounded-xl p-4 mt-6">
                  <h3 className="font-black text-neutral-900 dark:text-white mb-3">Job Summary</h3>
                  <div className="space-y-2 text-neutral-700 dark:text-neutral-300">
                    <p><strong>Title:</strong> {formData.title}</p>
                    <p><strong>Location:</strong> {formData.location}</p>
                    <p><strong>Budget:</strong> ${formData.budget_min} - ${formData.budget_max}</p>
                    <p><strong>Duration:</strong> {formData.duration || 'Not specified'}</p>
                    <p><strong>Skills:</strong> {formData.required_skills.join(', ') || 'None specified'}</p>
                  </div>
                </div>
              </div>

              <label className="mt-4 flex items-start gap-3 p-4 rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 cursor-pointer" data-testid="featured-job-toggle">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData(p => ({ ...p, featured: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-amber-500"
                  data-testid="featured-job-checkbox"
                />
                <span className="text-sm">
                  <span className="font-bold text-neutral-900 dark:text-white">⭐ Feature this job</span>
                  <span className="block text-xs text-neutral-500 mt-0.5">Featured jobs are pinned to the top of the job board with a highlight badge. Included with Contractor Pro (applied automatically if you're on Pro).</span>
                </span>
              </label>

              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 h-14 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold text-lg rounded-xl border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-16 flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                  data-testid="submit-job-btn"
                >
                  {loading ? (
                    <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Posting...</>
                  ) : (
                    <><PaperPlaneTilt size={24} weight="bold" /> Post Job</>
                  )}
                </button>
              </div>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate}
                className="w-full h-11 mt-3 flex items-center justify-center gap-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-bold text-sm rounded-xl border-2 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all disabled:opacity-50"
                data-testid="save-as-template-btn"
              >
                {savingTemplate ? <SpinnerGap size={16} weight="bold" className="animate-spin" /> : <Copy size={16} weight="bold" />}
                {savingTemplate ? 'Saving...' : 'Save as Template'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PostJob;
